import { useCallback, useEffect, useRef, useState } from "react"
import { ErrorToast } from "@ciq/shared/utils/toast"
import { createFloorPlanUploadJob, uploadFileToS3, submitFloorPlanJob, pollJobUntilComplete } from "../api"

let uploadIdCounter = 0
function generateUploadId() {
  uploadIdCounter += 1
  return `upload-${Date.now()}-${uploadIdCounter}`
}

/**
 * @typedef {Object} UploadItem
 * @property {string} id - Unique client-side upload ID (not filename — handles duplicates)
 * @property {string} filename - Original filename (for display only)
 * @property {"uploading"|"complete"|"error"} status - Upload status
 * @property {number} progress - Upload progress percentage (0-100)
 * @property {string|null} error - Error message if status is error
 * @property {File} file - Original File object (for retry)
 * @property {string|null} buildingZoneId - Building zone ID at time of upload (for retry)
 */

/**
 * @typedef {Object} UseFileUploadResult
 * @property {UploadItem[]} uploads - Current upload items with status
 * @property {boolean} isUploading - Boolean indicating if any files are currently uploading
 * @property {(files: File[]) => Promise<void>} uploadFiles - Start uploading files
 * @property {(id: string) => Promise<void>} retryFile - Retry a failed upload (creates new job)
 * @property {(id: string) => void} dismissFile - Remove a failed upload from the list
 */

/**
 * Derives the backend file_type from a filename extension.
 * Maps to UploadedFileType enum values: "pdf", "png", "jpg", "docx".
 * @param {string} filename - Original filename
 * @returns {string} Backend file type value, or empty string if no extension
 */
function deriveFileType(filename) {
  const dotIdx = filename.lastIndexOf(".")
  if (dotIdx === -1) return ""
  const ext = filename.slice(dotIdx + 1).toLowerCase()
  if (ext === "jpeg") return "jpg"
  return ext
}

/**
 * Derives building_zone_id from the sidebar selection.
 * Resolves floor selections to their parent building.
 * Inbox and non-building/floor selections return null.
 * @param {string|null} selectedItemId - Current sidebar selection
 * @param {Array} sidebarItems - Sidebar items (buildings with nested floors)
 * @returns {string|null} Building zone ID or null
 */
function deriveBuildingZoneId(selectedItemId, sidebarItems) {
  if (!selectedItemId || selectedItemId === "inbox") return null
  for (const item of sidebarItems || []) {
    if (item.id === selectedItemId && item.type === "building") return item.id
    if (item.type === "building" && item.floors?.some(f => f.id === selectedItemId)) return item.id
  }
  return null
}

/**
 * Controller hook for floor plan file upload orchestration.
 * Manages the full upload lifecycle: create job → S3 PUT with progress → submit job.
 * File records are created by the backend during job processing.
 *
 * @param {Object} params
 * @param {string} params.schoolId - School ID
 * @param {string|null} [params.selectedItemId] - Current sidebar selection (inbox, building, or floor)
 * @param {Array} [params.sidebarItems] - Sidebar items for building_zone_id derivation
 * @param {() => void} [params.onUploadComplete] - Callback when all files in a batch complete
 * @returns {UseFileUploadResult} Hook result with uploads state and actions
 */
export function useFileUpload({ schoolId, selectedItemId, sidebarItems, onUploadComplete }) {
  const [uploads, setUploads] = useState([])
  const abortControllerRef = useRef(null)

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  const updateUpload = useCallback((id, updates) => {
    setUploads(prev =>
      prev.map(u => (u.id === id ? { ...u, ...updates } : u))
    )
  }, [])

  const uploadFiles = useCallback(async (files) => {
    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller
    const signal = controller.signal

    const buildingZoneId = deriveBuildingZoneId(selectedItemId, sidebarItems)

    const uploadItems = files.map(f => ({
      id: generateUploadId(),
      filename: f.name,
      status: "uploading",
      progress: 0,
      error: null,
      file: f,
      buildingZoneId,
    }))

    setUploads(prev => [...prev, ...uploadItems])

    const fileInputs = files.map(f => ({
      filename: f.name,
      file_type: deriveFileType(f.name),
      building_zone_id: buildingZoneId,
    }))

    let job
    try {
      job = await createFloorPlanUploadJob(schoolId, fileInputs)
    } catch (err) {
      if (err.name === "AbortError") return
      const is409 = err?.response?.status === 409
      const errorMsg = is409
        ? "An active upload job already exists. Please wait for it to complete."
        : "Upload failed: unable to start upload. Please try again."
      ErrorToast(errorMsg)
      uploadItems.forEach(u => {
        updateUpload(u.id, { status: "error", error: errorMsg })
      })
      return
    }

    if (job.uploads.length !== files.length) {
      const errorMsg = "Upload failed: server error. Please try again."
      ErrorToast(errorMsg)
      uploadItems.forEach(u => updateUpload(u.id, { status: "error", error: errorMsg }))
      return
    }

    // Verify backend returned filenames in the same order as input files
    const mismatch = job.uploads.some((info, i) => info.filename !== files[i].name)
    if (mismatch) {
      const errorMsg = "Upload failed: server error. Please try again."
      ErrorToast(errorMsg)
      uploadItems.forEach(u => updateUpload(u.id, { status: "error", error: errorMsg }))
      return
    }

    const failedIds = new Set()

    const uploadPromises = job.uploads.map((uploadInfo, i) => {
      const item = uploadItems[i]
      return uploadFileToS3(uploadInfo.upload_url, files[i], (progress) => {
        updateUpload(item.id, { progress })
      }, signal)
        .then(() => {
          updateUpload(item.id, { status: "complete", progress: 100 })
        })
        .catch((err) => {
          if (err.name === "AbortError") return
          failedIds.add(item.id)
          updateUpload(item.id, { status: "error", error: err.message })
        })
    })

    await Promise.allSettled(uploadPromises)

    if (signal.aborted) return

    if (failedIds.size > 0) {
      ErrorToast("Some files failed to upload. Fix errors and retry.")
      return
    }

    try {
      await submitFloorPlanJob(schoolId, job.job_id)
    } catch (err) {
      if (err.name === "AbortError") return
      const errorMsg = err.handled ? err.message : "Failed to submit upload job for processing"
      uploadItems.forEach(u => updateUpload(u.id, { status: "error", error: errorMsg }))
      if (!err.handled) ErrorToast(errorMsg)
      return
    }

    try {
      const jobResult = await pollJobUntilComplete(schoolId, job.job_id, signal)
      if (jobResult.status !== "COMPLETED") {
        const errorMsg = "Upload processing failed. Please try again."
        uploadItems.forEach(u => updateUpload(u.id, { status: "error", error: errorMsg }))
        ErrorToast(errorMsg)
        return
      }
      setUploads(prev => prev.filter(u => u.status !== "complete"))
      onUploadComplete?.()
    } catch (err) {
      if (err.name === "AbortError") return
      const errorMsg = err.handled ? err.message : err.message
      uploadItems.forEach(u => updateUpload(u.id, { status: "error", error: errorMsg }))
      if (!err.handled) ErrorToast(errorMsg)
    }
  }, [schoolId, selectedItemId, sidebarItems, updateUpload, onUploadComplete])

  const retryFile = useCallback(async (id) => {
    const upload = uploads.find(u => u.id === id)
    if (!upload || upload.status !== "error") return

    const controller = new AbortController()
    abortControllerRef.current = controller
    const signal = controller.signal

    updateUpload(id, { status: "uploading", progress: 0, error: null })

    const buildingZoneId = upload.buildingZoneId

    try {
      const job = await createFloorPlanUploadJob(schoolId, [
        { filename: upload.filename, file_type: deriveFileType(upload.filename), building_zone_id: buildingZoneId },
      ])
      if (!job.uploads?.length) {
        updateUpload(id, { status: "error", error: "Upload failed: server error. Please try again." })
        return
      }
      const uploadInfo = job.uploads[0]

      await uploadFileToS3(uploadInfo.upload_url, upload.file, (progress) => {
        updateUpload(id, { progress })
      }, signal)
      updateUpload(id, { status: "complete", progress: 100 })

      await submitFloorPlanJob(schoolId, job.job_id)
      const jobResult = await pollJobUntilComplete(schoolId, job.job_id, signal)
      if (jobResult.status !== "COMPLETED") {
        const errorMsg = "Upload processing failed. Please try again."
        updateUpload(id, { status: "error", error: errorMsg })
        ErrorToast(errorMsg)
        return
      }
      setUploads(prev => prev.filter(u => u.id !== id))
      onUploadComplete?.()
    } catch (err) {
      if (err.name === "AbortError") return
      updateUpload(id, { status: "error", error: err.message })
      if (err.handled) return
      ErrorToast(`Failed to upload "${upload.filename}"`)
    }
  }, [uploads, schoolId, updateUpload, onUploadComplete])

  const dismissFile = useCallback((id) => {
    setUploads(prev => prev.filter(u => u.id !== id))
  }, [])

  const isUploading = uploads.some(u => u.status === "uploading")

  return { uploads, isUploading, uploadFiles, retryFile, dismissFile }
}
